// Szolgáltatás-katalógus HÁROMSZINTŰ szerkesztő tiszta segédei (nem-komponens): a form-értékek típusai
// (service → plan → price), a kategória-kaszkád mátrix, a pricingType→priceType mátrix, a betöltött detail →
// form-értékek leképezés, a form → wire-body (nested) építő, és a kliens-oldali zod-resolver-séma-gyár. Külön
// fájl a komponenstől (Fast Refresh + tesztelhető tiszta függvények). A minta a features/service-assignments/form.ts.
//
// A VÉGSŐ validáló-fal a szerver (BFF/Odin) — itt csak elő-ellenőrzés + a mátrix/monotonitás UI-jelzése van. A
// pénz DECIMAL-STRING (soha float); a tier-mezők egészek. A `simple` szolgáltatás plan-neve a szolgáltatás
// nevéből öröklődik (a wire-builder tölti); a `package` plan-nevek egyediek.
import {
  SUBCATEGORIES_BY_MAIN,
  type ServiceCatalogDetailSchema,
  type ServiceCatalogWriteSchema,
} from '@atlas/contracts';
import { type TFunction } from 'i18next';
import { type z } from 'zod';
import { z as zod } from 'zod';

import {
  buildPriceRows,
  CURRENCIES,
  PRICING_TYPES,
  type PricingFormValues,
  type PricingType,
  pricesToForm,
  validatePricingForm,
  VAT_RATES,
  type VatRate,
} from '@/lib/pricing-form';

// A KÖZÖS árazó-primitívek a `@/lib/pricing-form`-ban élnek (a katalógus ÉS a hozzárendelés-ár-override közös
// rétege) — innen RE-EXPORTÁLVA, hogy a katalógus-modul meglévő importjai (plan-editor, tesztek) ne törjenek
// (CURRENCIES, Currency, PRICING_TYPES, PricingType, VAT_RATES, VatRate, TierFormValues, emptyTier, hasTiers,
// primaryPriceType, buildPriceRows, pricesToForm, PricingFormValues).
export * from '@/lib/pricing-form';

type Detail = z.infer<typeof ServiceCatalogDetailSchema>;
type WriteBody = z.input<typeof ServiceCatalogWriteSchema>;
type PlanWire = WriteBody['plans'][number];

// ── Katalógus-specifikus enum-listák (nem-árazó dimenziók) ──
export const BILLING_TYPES = ['recurring', 'one_time'] as const;
export const MAIN_CATEGORIES = ['product', 'advisory', 'other'] as const;
export const SUB_CATEGORIES = [
  'roomsome',
  'innovation',
  'digital_marketing',
  'revenue_management',
  'web_development',
  'other',
] as const;
export const USAGE_SOURCE_TYPES = [
  'newsletter',
  'loyalty_newsletter',
  'rs_loyalty',
  'rs_direct_referral',
  'rs_direct',
  'rs_referral',
  'rs_voucher',
  'ticket',
] as const;
export const STATUSES = ['active', 'inactive'] as const;
export const STRUCTURE_TYPES = ['simple', 'package'] as const;

/** A „nincs felelős ügyfélszolgálat" sentinel (a Radix Select nem enged üres value-t) — a wire-body-ban null. */
export const SUPPORT_TEAM_NONE = '__none__';

export type BillingType = (typeof BILLING_TYPES)[number];
export type MainCategory = (typeof MAIN_CATEGORIES)[number];
export type SubCategory = (typeof SUB_CATEGORIES)[number];
export type UsageSourceType = (typeof USAGE_SOURCE_TYPES)[number];
export type ServiceStatus = (typeof STATUSES)[number];
export type StructureType = (typeof STRUCTURE_TYPES)[number];

// ── Fő-kategória → engedélyezett alkategóriák: a SZERZŐDÉSBŐL (`@atlas/contracts`), mert a BFF is ezt
// érvényesíti (422). A korábbi lokális másolat elcsúszhatott volna a szerver-oldali faltól. ──
export { SUBCATEGORIES_BY_MAIN };

/** A főkategória első engedélyezett alkategóriája (a főkat-váltás auto-reset célja). */
export function firstSubCategory(main: MainCategory): SubCategory {
  return SUBCATEGORIES_BY_MAIN[main][0];
}

/** Látszik-e a usageSourceType választó (a használat-alapú pricingType-oknál: tiered / base_tiered / commission).
 *  A spec „commission/tiered"-et mond; a base_tiered ugyanígy külső-forrásból dolgozik → azt is beengedjük (a mező
 *  opcionális, nullable — nincs kár). */
export function showsUsageSource(pt: PricingType): boolean {
  return pt === 'tiered' || pt === 'base_tiered' || pt === 'commission';
}

// ── Form-érték típusok ──

// A KÖZÖS árazó-mezők (pricingType/primaryPrice/tiers/overflow) a `PricingFormValues`-ból öröklődnek — a csomag
// ezt bővíti a nem-árazó (név/kategória/számla) dimenziókkal.
export interface PlanFormValues extends PricingFormValues {
  /** Meglévő plan azonosítója (edit-diff); új plannél undefined. */
  id?: string;
  /** OCC-verzió (edit-diff PUT If-Match); új plannél undefined. */
  version?: number;
  nameHu: string;
  nameEn: string;
  mainCategory: MainCategory;
  subCategory: SubCategory;
  billingType: BillingType;
  /** '' = nincs megadva → az adatban null. */
  usageSourceType: UsageSourceType | '';
  vatRate: VatRate;
  invoiceItemName: string;
  invoiceItemNote: string;
}

export interface ServiceCatalogFormValues {
  nameHu: string;
  nameEn: string;
  descriptionHu: string;
  descriptionEn: string;
  status: ServiceStatus;
  showInTicketDropdown: boolean;
  structureType: StructureType;
  /** Felelős ügyfélszolgálat (support-team id) VAGY a `SUPPORT_TEAM_NONE` sentinel (nincs kijelölve). */
  supportTeamId: string;
  /** A dashboard-chart csoportosítás flag — a UI-n nincs mező, de a body-ban megőrizzük (whitelist-merge). */
  groupedInCharts: boolean;
  plans: PlanFormValues[];
}

/** Üres csomag (create-default): egyszerű, ismétlődő, fix áras, ÁFA 27%, HUF, egyetlen üres primary-árral. */
export function emptyPlan(): PlanFormValues {
  return {
    nameHu: '',
    nameEn: '',
    mainCategory: 'other',
    subCategory: 'other',
    billingType: 'recurring',
    pricingType: 'flat',
    usageSourceType: '',
    vatRate: 'VAT_27',
    invoiceItemName: '',
    invoiceItemNote: '',
    primaryPrice: '',
    primaryCurrency: 'HUF',
    tiers: [],
    overflowEnabled: false,
    overflowBlockSize: '',
    overflowPrice: '',
    overflowCurrency: 'HUF',
  };
}

/** Üres form (create-mód): egyszerű szolgáltatás, aktív, jegy-dropdownban látszik, egyetlen üres csomaggal. */
export const EMPTY_SERVICE_CATALOG_FORM: ServiceCatalogFormValues = {
  nameHu: '',
  nameEn: '',
  descriptionHu: '',
  descriptionEn: '',
  status: 'active',
  showInTicketDropdown: true,
  structureType: 'simple',
  supportTeamId: SUPPORT_TEAM_NONE,
  groupedInCharts: false,
  plans: [emptyPlan()],
};

const asEnum = <T extends string>(
  values: readonly T[],
  v: string | null | undefined,
  fallback: T,
): T => (values.includes(v as T) ? (v as T) : fallback);

// ── detail → form (edit-mód előtöltés) ──

function planToForm(plan: Detail['plans'][number]): PlanFormValues {
  const pricingType = asEnum(PRICING_TYPES, plan.pricingType, 'flat');
  const mainCategory = asEnum(MAIN_CATEGORIES, plan.mainCategory, 'other');
  return {
    ...(plan.id ? { id: plan.id } : {}),
    version: plan.version,
    nameHu: plan.name.hu,
    nameEn: plan.name.en,
    mainCategory,
    subCategory: asEnum(SUB_CATEGORIES, plan.subCategory, firstSubCategory(mainCategory)),
    billingType: asEnum(BILLING_TYPES, plan.billingType, 'recurring'),
    pricingType,
    usageSourceType: USAGE_SOURCE_TYPES.find((u) => u === plan.usageSourceType) ?? '',
    vatRate: asEnum(VAT_RATES, plan.vatRate, 'VAT_27'),
    invoiceItemName: plan.invoiceItemName ?? '',
    invoiceItemNote: plan.invoiceItemNote ?? '',
    // A KÖZÖS árazó-mezők (primaryPrice/tiers/overflow) a shared leképezőből (pricingType-vezérelt mátrix).
    ...pricesToForm(plan.prices, pricingType),
  };
}

/** A betöltött TELJES detail → form-értékek (edit-mód). A plans[]+prices[] a nested szerkesztő-egységbe bomlik. */
export function detailToFormValues(detail: Detail): ServiceCatalogFormValues {
  return {
    nameHu: detail.name.hu,
    nameEn: detail.name.en,
    descriptionHu: detail.description?.hu ?? '',
    descriptionEn: detail.description?.en ?? '',
    status: asEnum(STATUSES, detail.status, 'active'),
    showInTicketDropdown: detail.showInTicketDropdown,
    structureType: asEnum(STRUCTURE_TYPES, detail.structureType, 'simple'),
    supportTeamId: detail.supportTeamId ?? SUPPORT_TEAM_NONE,
    groupedInCharts: detail.groupedInCharts,
    plans: detail.plans.map(planToForm),
  };
}

// ── form → wire-body (nested create/update) ──

const trimOrNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());

function planToWire(plan: PlanFormValues, service: ServiceCatalogFormValues): PlanWire {
  // simple → a plan neve a szolgáltatás nevéből öröklődik (legacy 1:1); package → egyedi plan-név.
  const name =
    service.structureType === 'simple'
      ? { hu: service.nameHu.trim(), en: service.nameEn.trim() }
      : { hu: plan.nameHu.trim(), en: plan.nameEn.trim() };
  return {
    ...(plan.id ? { id: plan.id } : {}),
    name,
    mainCategory: plan.mainCategory,
    subCategory: plan.subCategory,
    billingType: plan.billingType,
    pricingType: plan.pricingType,
    usageSourceType:
      showsUsageSource(plan.pricingType) && plan.usageSourceType !== ''
        ? plan.usageSourceType
        : null,
    vatRate: plan.vatRate,
    // kötelező (a form-séma nem engedi üresen tovább) — trim-elve megy
    invoiceItemName: plan.invoiceItemName.trim(),
    invoiceItemNote: trimOrNull(plan.invoiceItemNote),
    prices: buildPriceRows(plan),
  };
}

/** A form → nested wire-body (create + update közös). A currency-t MINDIG írjuk (a legacy bug: HUF-ra esett). */
export function formToWriteBody(values: ServiceCatalogFormValues): WriteBody {
  const hasDesc = values.descriptionHu.trim() !== '' || values.descriptionEn.trim() !== '';
  return {
    name: { hu: values.nameHu.trim(), en: values.nameEn.trim() },
    description: hasDesc ? { hu: values.descriptionHu, en: values.descriptionEn } : null,
    structureType: values.structureType,
    status: values.status,
    showInTicketDropdown: values.showInTicketDropdown,
    // A sentinel → null (nincs felelős ügyfélszolgálat); egyébként a kiválasztott csapat id-ja.
    supportTeamId: values.supportTeamId === SUPPORT_TEAM_NONE ? null : values.supportTeamId,
    groupedInCharts: values.groupedInCharts,
    plans: values.plans.map((p) => planToWire(p, values)),
  };
}

// ── Kliens-oldali resolver-séma (elő-ellenőrzés; a szerver a végső fal) ──

const tierSchema = zod.object({
  tierMin: zod.string(),
  tierMax: zod.string(),
  netPrice: zod.string(),
  currency: zod.enum(CURRENCIES),
});

const planSchema = zod.object({
  id: zod.string().optional(),
  version: zod.number().optional(),
  nameHu: zod.string(),
  nameEn: zod.string(),
  mainCategory: zod.enum(MAIN_CATEGORIES),
  subCategory: zod.enum(SUB_CATEGORIES),
  billingType: zod.enum(BILLING_TYPES),
  pricingType: zod.enum(PRICING_TYPES),
  usageSourceType: zod.string(),
  vatRate: zod.enum(VAT_RATES),
  invoiceItemName: zod.string().max(255),
  invoiceItemNote: zod.string().max(1000),
  primaryPrice: zod.string(),
  primaryCurrency: zod.enum(CURRENCIES),
  tiers: zod.array(tierSchema),
  overflowEnabled: zod.boolean(),
  overflowBlockSize: zod.string(),
  overflowPrice: zod.string(),
  overflowCurrency: zod.enum(CURRENCIES),
});

/**
 * A háromszintű form kliens-oldali validálása. A `t` a séma-gyárba injektálva → a mátrix/monotonitás-üzeneteket
 * KÖZVETLENÜL fordítva adjuk (a nested tömb-utak — plans.i.tiers.j.* — miatt az errorMap-alapú fordítás
 * kétértelmű lenne). A `structureType`-ot a hívó adja (a plan-név csak package-nél kötelező). A szerver a
 * végső fal (a BFF a currency-t is kényszeríti, package→simple tiltás stb.).
 */
export function buildServiceCatalogSchema(t: TFunction): z.ZodType {
  const v = (k: string): string => t(`validation:serviceCatalog.${k}`);
  return zod
    .object({
      nameHu: zod.string().trim().min(1, v('nameRequired')).max(255, v('tooLong')),
      nameEn: zod.string().trim().min(1, v('nameRequired')).max(255, v('tooLong')),
      descriptionHu: zod.string().max(500, v('tooLong')),
      descriptionEn: zod.string().max(500, v('tooLong')),
      status: zod.enum(STATUSES),
      showInTicketDropdown: zod.boolean(),
      structureType: zod.enum(STRUCTURE_TYPES),
      supportTeamId: zod.string(),
      groupedInCharts: zod.boolean(),
      plans: zod.array(planSchema).min(1),
    })
    .superRefine((values, ctx) => {
      values.plans.forEach((plan, i) => {
        const base = ['plans', i] as const;
        // Package → a plan-név mindkét nyelven kötelező (simple-nél a szolgáltatás nevéből öröklődik).
        if (values.structureType === 'package') {
          if (plan.nameHu.trim() === '') {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              path: [...base, 'nameHu'],
              message: v('planNameRequired'),
            });
          }
          if (plan.nameEn.trim() === '') {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              path: [...base, 'nameEn'],
              message: v('planNameRequired'),
            });
          }
        }
        // A SZÁMLATÉTEL NEVE KÖTELEZŐ (user-döntés): üresen a hozzárendelés pillanatképe is üres maradt, és a
        // számlára egy azóta átnevezett csomagnév került. A szerver is kikényszeríti (szerződés, 422).
        if (plan.invoiceItemName.trim() === '') {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            path: [...base, 'invoiceItemName'],
            message: v('invoiceItemNameRequired'),
          });
        }
        // one_time → csak flat (a UI zárolja, de kliens-oldalon is jelezzük).
        if (plan.billingType === 'one_time' && plan.pricingType !== 'flat') {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            path: [...base, 'pricingType'],
            message: v('oneTimeFlat'),
          });
        }
        // Elsődleges ár + sávok + overflow — a KÖZÖS árazó-validátor (a mátrix/monotonitás egy helyen).
        validatePricingForm(plan, ctx, [...base], v);
      });
    });
}
